const COOKIE_NAME = 'coretas_attribution';
const SITE_DOMAIN = 'coretas.ai';
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const UTM_PREFIX = 'utm_';
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
// Mirrors the backend allowlist in app/services/acquisition/service.py.
const MAX_CLICK_ID_LENGTH = 256;
const MAX_UTM_LENGTH = 128;
const MAX_KEYS = 12;

const isUtm = (key: string): boolean => key.startsWith(UTM_PREFIX);

const isTracked = (key: string): boolean =>
  (CLICK_ID_KEYS as readonly string[]).includes(key) ||
  key.startsWith(UTM_PREFIX);

const readCookie = (): URLSearchParams => {
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COOKIE_NAME}=`));
  return new URLSearchParams(match ? match.slice(COOKIE_NAME.length + 1) : '');
};

// Shares a cookie between coretas.ai and the app; a host with no domain to share takes none.
export const siteCookieDomain = (hostname: string): string =>
  hostname === SITE_DOMAIN || hostname.endsWith(`.${SITE_DOMAIN}`)
    ? `; domain=.${SITE_DOMAIN}`
    : '';

const fromUrl = (search: string): Map<string, string> => {
  const found = new Map<string, string>();
  for (const [rawKey, rawValue] of new URLSearchParams(search)) {
    const key = rawKey.trim().toLowerCase();
    const value = rawValue.trim();
    // An empty parameter never clears a stored click id: a bookmark return carries none.
    const cap = isUtm(key) ? MAX_UTM_LENGTH : MAX_CLICK_ID_LENGTH;
    if (!value || value.length > cap || !isTracked(key)) continue;
    found.set(key, value);
  }
  return found;
};

export const captureAttribution = (
  search: string = window.location.search,
): void => {
  try {
    const found = fromUrl(search);
    if (found.size === 0) return;

    const stored = readCookie();
    for (const [key, value] of found) stored.set(key, value);

    const kept = [...stored.entries()]
      .filter(([key]) => isTracked(key))
      // A click id is the one value Google can match; it outranks any utm_ on the cap.
      .sort(([a], [b]) => Number(isUtm(a)) - Number(isUtm(b)));
    const cookie = new URLSearchParams(kept.slice(0, MAX_KEYS)).toString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${cookie}; path=/${siteCookieDomain(window.location.hostname)}; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Attribution is never worth breaking a page load over.
  }
};

export const clearAttribution = (): void => {
  document.cookie = `${COOKIE_NAME}=; path=/${siteCookieDomain(window.location.hostname)}; max-age=0; SameSite=Lax`;
};
